//DevTools detection and request obfuscation
import { config } from "../../config";
import Logger from "./logger";

class NetworkProtection {
  private static isDevToolsOpen = false;
  private static devToolsWarningShown = false;
  private static requestCounter = 0;

  static initDevToolsDetection() {
    // Check if network protection is enabled
    if (!config.enableNetworkProtection) {
      if (config.enableDebug) {
        Logger.log("Network protection disabled by configuration");
      }
      return;
    }

    const isDevelopment = config.isDevelopment();

    if (isDevelopment) {
      if (config.enableDebug) {
        Logger.log("Network protection active (development mode)");
      }
      return;
    }

    let devtools = {
      open: false,
      orientation: null,
    };

    const threshold = 160;

    setInterval(() => {
      if (
        window.outerHeight - window.innerHeight > threshold ||
        window.outerWidth - window.innerWidth > threshold
      ) {
        if (!devtools.open) {
          devtools.open = true;
          this.onDevToolsOpened();
        }
      } else {
        if (devtools.open) {
          devtools.open = false;
          this.onDevToolsClosed();
        }
      }
    }, 500);

    let element = new Image();
    Object.defineProperty(element, "id", {
      get: () => {
        this.onDevToolsOpened();
        return "devtools-detected";
      },
    });

    setInterval(() => {
      const before = Date.now();
      debugger;
      const after = Date.now();
      if (after - before > 100) {
        this.onDevToolsOpened();
      }
    }, 3000);
  }

  private static onDevToolsOpened() {
    this.isDevToolsOpen = true;

    if (!this.devToolsWarningShown) {
      this.showSecurityWarning();
      this.devToolsWarningShown = true;
    }

    this.enableEnhancedSecurity();
  }

  private static onDevToolsClosed() {
    this.isDevToolsOpen = false;

    this.devToolsWarningShown = false;
    Logger.log("DevTools closed - normal security mode");
  }

  private static showSecurityWarning() {
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    ) {
      return;
    }

    Logger.warn(`
    SECURITY NOTICE - MARS System
    =============================
    
    This is a secure educational system protected by various security measures.
    
    Unauthorized access attempts are monitored and logged.
    
    If you are a authorized developer, please use the official development environment.
    
    For security concerns, contact: security@mars-system.edu
    `);

    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:"
    ) {
    }
  }

  private static enableEnhancedSecurity() {
    Logger.log("Enhanced security mode activated");
  }

  // Add noise to network requests to confuse analyzers - CORS safe
  static addRequestNoise(): Record<string, string> {
    const noiseHeaders: Record<string, string> = {};

    // Skip noise headers in development or if network protection is disabled
    if (!config.enableNetworkProtection || config.isDevelopment()) {
      return noiseHeaders;
    }

    // In production, add minimal CORS-safe headers only if DevTools detected
    if (this.isDevToolsOpen) {
      noiseHeaders["X-Requested-With"] = "XMLHttpRequest";
    }

    return noiseHeaders;
  }

  // HTTPS enforcement
  static enforceHTTPS() {
    // Only enforce HTTPS if configured to do so
    if (!config.enforceHttps) {
      return;
    }

    if (window.location.protocol !== "https:" && !config.isDevelopment()) {
      if (config.enableDebug) {
        Logger.warn("Insecure connection detected. Redirecting to HTTPS...");
      }
      window.location.href = window.location.href.replace("http:", "https:");
    }
  }

  static initializeProtection() {
    if (
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"
    ) {
      this.enforceHTTPS();
    }

    // Initialize DevTools detection based on configuration
    this.initDevToolsDetection();

    if (config.enableDebug) {
      const mode = config.isDevelopment() ? "development" : "production";
      const protectionStatus = config.enableNetworkProtection
        ? "enabled"
        : "disabled";
      Logger.log(
        `Network protection initialized (${mode} mode - ${protectionStatus})`
      );
    }
  }

  static isSecureContext(): boolean {
    return window.isSecureContext || window.location.protocol === "https:";
  }

  static getSecurityStatus() {
    return {
      httpsEnabled: window.location.protocol === "https:",
      devToolsDetected: this.isDevToolsOpen,
      secureContext: this.isSecureContext(),
      requestCount: this.requestCounter,
    };
  }
}

export default NetworkProtection;
